import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Back from "../../button/back";

const JurnalGuru = () => {
  /* ================= STATE ================= */
  const [dataTP, setDataTP] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const { id } = useParams();
  const [datakepsek, setDataKepsek] = useState([]);
  const [sekolah, setSekolah] = useState([]);

  /* ================= FILTER ================= */
  const [mapel, setMapel] = useState("");
  const [semester, setSemester] = useState("");
  const [kelas, setKelas] = useState("");
  const [rombel, setRombel] = useState("");

  const [isSearch, setIsSearch] = useState(false);

  /* ================= TAHUN PELAJARAN ================= */
  const getTahunPelajaran = () => {
    const now = new Date().getFullYear();
    if (semester === "1") return `${now}/${now + 1}`;
    if (semester === "2") return `${now - 1}/${now}`;
    return "-";
  };

  /* ================= DATA SEKOLAH ================= */
  useEffect(() => {
    const fetchSchoolData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}api/datasekolah/all`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSekolah(res.data || []);
      } catch {
        setError("Gagal mengambil data sekolah");
      } finally {
        setLoading(false);
      }
    };
    fetchSchoolData();
  }, []);

  /* ================= USER ================= */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");

    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setUser(res.data))
      .catch(() => navigate("/login"));
  }, [navigate, id]);

  /* ================= KEPSEK ================= */
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/auth/user-images-and-roles`)
      .then((res) =>
        setDataKepsek(res.data.filter((u) => u.jabatan === "Kepala Sekolah")),
      )
      .catch(() => setError("Gagal mengambil data kepala sekolah"));
  }, []);

  /* ================= DATA JURNAL ================= */
  useEffect(() => {
    const fetchTP = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}api/tp`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDataTP(res.data || []);
      } catch {
        setError("Gagal mengambil data jurnal");
      } finally {
        setLoading(false);
      }
    };
    fetchTP();
  }, []);

  /* ================= OPSI FILTER ================= */
  const opsiMapel = [...new Set(dataTP.map((d) => d.mapel))];
  const opsiSemester = [...new Set(dataTP.map((d) => d.semester))];
  const opsiKelas = [...new Set(dataTP.map((d) => d.kelas))];
  const opsiRombel = [...new Set(dataTP.map((d) => d.rombel))];

  const getTanggalIndonesia = () => {
    const now = new Date();

    const bulan = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember",
    ];

    return `${now.getDate()} ${bulan[now.getMonth()]} ${now.getFullYear()}`;
  };

  const subjectMapping = {
    PAI: "Pendidikan Agama dan Budi Pekerti",
    PKN: "Pendidikan Pancasila",
    BIndo: "Bahasa Indonesia",
    MTK: "Matematika",
    IPAS: "Ilmu Pengetahuan Alam dan Sosial",
    PJOK: "Pendidikan Jasmani Olahraga dan Kesehatan",
    SENIBUDAYA: "Seni dan Budaya",
    BIng: "Bahasa Inggris",
    MULOK: "Muatan Lokal",
    Koding: "Koding dan Kecerdasan Artifisial",
  };

  const getSemesterText = (semester) => {
    switch (String(semester)) {
      case "1":
        return "1 (satu)";
      case "2":
        return "2 (dua)";
      default:
        return semester || "-";
    }
  };

  const getKelasText = (kelas) => {
    switch (kelas) {
      case "1":
      case "2":
        return "A";
      case "3":
      case "4":
        return "B";
      case "5":
      case "6":
        return "C";
      default:
        return "-";
    }
  };

  const getNamaMapel = (kode) => {
    return subjectMapping[kode] || kode || "-";
  };

  /* ================= GROUP DATA ================= */
  const jurnalGrouped = useMemo(() => {
    if (!isSearch) return {};

    const grouped = {};
    dataTP.forEach((tp) => {
      if (
        tp.mapel !== mapel ||
        tp.semester !== semester ||
        tp.kelas !== kelas ||
        tp.rombel !== rombel
      )
        return;

      tp.TP?.forEach((itemTP) => {
        if (!grouped[itemTP.isiTP]) grouped[itemTP.isiTP] = [];
        itemTP.prosesTP?.forEach((p) => grouped[itemTP.isiTP].push(p));
      });
    });
    return grouped;
  }, [dataTP, mapel, semester, kelas, rombel, isSearch]);

  /* ================= DOWNLOAD PDF ================= */
  const handleDownloadPDF = async () => {
    const element = document.getElementById("laporan-pdf");

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("l", "mm", "a4");

    // ================== SETTING ==================
    const margin = {
      top: 15,
      bottom: 15,
      left: 15,
      right: 15,
    };

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const usableWidth = pageWidth - margin.left - margin.right;
    const usableHeight = pageHeight - margin.top - margin.bottom;

    const imgHeight = (canvas.height * usableWidth) / canvas.width;

    let heightLeft = imgHeight;
    let positionY = margin.top;

    // ================== PAGE 1 ==================
    pdf.addImage(
      imgData,
      "PNG",
      margin.left,
      positionY,
      usableWidth,
      imgHeight,
    );

    heightLeft -= usableHeight;

    // ================== PAGE NEXT ==================
    while (heightLeft > 0) {
      pdf.addPage();
      positionY = margin.top - (imgHeight - heightLeft);

      pdf.addImage(
        imgData,
        "PNG",
        margin.left,
        positionY,
        usableWidth,
        imgHeight,
      );

      heightLeft -= usableHeight;
    }

    pdf.save("Laporan_Guru.pdf");
  };

  /* ================= VALIDASI ================= */
  const handleCari = () => {
    if (!mapel || !semester || !kelas || !rombel)
      return alert("Lengkapi semua filter");
    setIsSearch(true);
  };

  if (loading) return <p className="text-center py-20">Loading...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  /* ================= JSX ================= */
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-center mb-6">
        Jurnal Kegiatan Guru
      </h1>

      {/* FILTER */}
      <div className="grid md:grid-cols-5 gap-4 mb-4">
        <select
          value={mapel}
          onChange={(e) => setMapel(e.target.value)}
          className="border p-2"
        >
          <option value="">Pilih Mata Pelajaran</option>
          {opsiMapel.map((m, i) => (
            <option key={i} value={m}>
              {getNamaMapel(m)}
            </option>
          ))}
        </select>

        <select
          onChange={(e) => setSemester(e.target.value)}
          className="border p-2"
        >
          <option value="">Semester</option>
          {opsiSemester.map((s, i) => (
            <option key={i}>{s}</option>
          ))}
        </select>
        <select
          onChange={(e) => setKelas(e.target.value)}
          className="border p-2"
        >
          <option value="">Kelas</option>
          {opsiKelas.map((k, i) => (
            <option key={i}>{k}</option>
          ))}
        </select>
        <select
          onChange={(e) => setRombel(e.target.value)}
          className="border p-2"
        >
          <option value="">Rombel</option>
          {opsiRombel.map((r, i) => (
            <option key={i}>{r}</option>
          ))}
        </select>
        <button onClick={handleCari} className="bg-blue-600 text-white rounded">
          Cari
        </button>
      </div>

      {isSearch && (
        <button
          onClick={handleDownloadPDF}
          className="bg-green-600 text-white px-4 py-2 rounded mb-4"
        >
          Download PDF
        </button>
      )}

      {/* ================= AREA PDF ================= */}
      {isSearch && (
        <div id="laporan-pdf" className="bg-white p-6 mt-10">
          <h2 className="text-xl font-bold text-center uppercase">
            LAPORAN GURU {sekolah?.[0]?.namasekolah}
          </h2>

          <p className="text-xl text-center mb-6 font-bold uppercase ">
            Tahun Pelajaran {getTahunPelajaran()}
          </p>

          {/* IDENTITAS */}
          <div className="flex justify-between text-sm mb-4 px-2">
            {/* KIRI */}
            <div className="grid grid-cols-[140px_1fr] gap-y-1">
              <span>Mata Pelajaran</span>
              <span>: {getNamaMapel(mapel)}</span>

              <span>Kelas / Rombel</span>
              <span>
                : {kelas} {rombel}
              </span>
            </div>

            {/* KANAN */}
            <div className="grid grid-cols-[100px_1fr] gap-y-1">
              <span>Semester</span>
              <span>: {getSemesterText(semester)}</span>

              <span>Fase</span>
              <span>: {getKelasText(kelas)}</span>
            </div>
          </div>

          {/* TABEL */}
          <table className="w-full border text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 pb-3">No</th>
                <th className="border p-2 pb-3">Kompetensi Dasar/TP</th>
                <th className="border p-2 pb-3">Sub Kompetensi Dasar/TP</th>
                <th className="border p-2 pb-3">Tanggal</th>
                <th className="border p-2 pb-3">Materi</th>
                <th className="border p-2 pb-3">Kecapaian Materi</th>
                <th className="border p-2 pb-3">Permasalahan Dalam Proses</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(jurnalGrouped).map(([isiTP, list], i) =>
                list.map((p, idx) => (
                  <tr key={idx}>
                    {idx === 0 && (
                      <>
                        <td rowSpan={list.length} className="border p-2">
                          {i + 1}
                        </td>
                        <td rowSpan={list.length} className="border p-2">
                          {isiTP}
                        </td>
                      </>
                    )}
                    <td className="border p-2">{p.subTP || "-"}</td>
                    <td className="border p-2">
                      {p.tanggal
                        ? new Date(p.tanggal).toLocaleDateString("id-ID")
                        : "-"}
                    </td>
                    <td className="border p-2">{p.materi || "-"}</td>
                    <td className="border p-2">{p.kecapaian || "-"}</td>
                    <td className="border p-2">{p.permasalahan || "-"}</td>
                  </tr>
                )),
              )}
            </tbody>
          </table>

          {/* TANDA TANGAN */}
          <div className="flex justify-between mt-16 text-center px-24">
            <div>
              <div className="text-left inline-block">
                <br />
                <p>Mengetahui</p>
                <p>Kepala Sekolah</p>
                <br />
                <br />

                <p className="font-semibold inline-block text-[13px] ">
                  {datakepsek?.[0]?.name}
                </p>
                <div className="absolute w-[185px] border-t border-black mt-1"></div>
                <p className="font-semibold ">NIP. {datakepsek?.[0]?.NIP}</p>
              </div>
            </div>

            <div>
              <div className="text-left inline-block">
                <p>
                  {sekolah?.[0]?.ttDsekolah}, {getTanggalIndonesia()}
                </p>
                <br />
                <p>Guru Mata Pelajaran</p>
                <br />
                <br />
                <p className="font-semibold inline-block text-[13px] ">
                  {user?.name}
                </p>
                <div className="absolute w-[185px] border-t border-black mt-1"></div>
                <p className="font-semibold">NIP. {user?.NIP}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mt-10 flex justify-center">
        <Link to={`/profile/${id}`}>
          <Back />
        </Link>
      </div>
    </div>
  );
};

export default JurnalGuru;
